// Capacitor-free SQLite contract. Both the runtime connection (sqlite.ts, backed
// by @capacitor-community/sqlite) and the node-SQLite parity test implement this,
// so catalogReplica/catalogReplicaRepo never import the native plugin and run in
// either environment unchanged.
import type {
  EntityReplicaConfig,
  ReplicaColumnType,
} from './entityReplicaConfig.generated.ts'
import { USER_DATA_DDL } from './userDataTables.ts'

export type SqlValue = string | number | null
export type SqliteRow = Record<string, SqlValue>

export type SqliteExecutor = {
  run: (sql: string, params: SqlValue[]) => Promise<void>
  query: (sql: string, params: SqlValue[]) => Promise<SqliteRow[]>
  transaction: (work: () => Promise<void>) => Promise<void>
  // One statement executed once per params row. On the Capacitor executor this is
  // a single native bridge call (executeSet) — bulk writes like a blob bootstrap
  // are bridge-bound (~8ms per round-trip), so per-row run() calls take minutes
  // for a full catalog. Absent -> callers fall back to a run() loop.
  runBatch?: (sql: string, paramsList: SqlValue[][]) => Promise<void>
}

// Cap the rows per native call: one giant executeSet would serialize the whole
// catalog into a single bridge message (memory spike + main-thread jank); 500
// rows ≈ 1-2 MB of JSON keeps calls large enough to amortize the bridge but
// small enough to stay smooth.
const BATCH_CHUNK_ROWS = 500

export async function runManyRows(
  executor: SqliteExecutor,
  sql: string,
  paramsList: readonly SqlValue[][],
): Promise<void> {
  if (paramsList.length === 0) return
  if (executor.runBatch) {
    for (let start = 0; start < paramsList.length; start += BATCH_CHUNK_ROWS) {
      await executor.runBatch(sql, paramsList.slice(start, start + BATCH_CHUNK_ROWS))
    }
    return
  }
  for (const params of paramsList) await executor.run(sql, params)
}

const SQLITE_TYPE: Record<ReplicaColumnType, string> = {
  text: 'TEXT',
  int: 'INTEGER',
  numeric: 'REAL',
  bool: 'INTEGER',
}

export function tableDDL(config: EntityReplicaConfig): string {
  const columnDefs = config.columns.map((column) =>
    column.col === 'id'
      ? `"id" TEXT PRIMARY KEY NOT NULL`
      : `"${column.col}" ${SQLITE_TYPE[column.type]}`,
  )
  columnDefs.push(`"data" TEXT NOT NULL`)
  return `CREATE TABLE IF NOT EXISTS "${config.table}" (\n  ${columnDefs.join(',\n  ')}\n);`
}

export function indexDDL(config: EntityReplicaConfig): string[] {
  return config.indexes.map((columns) => {
    const name = `${config.table}_${columns.join('_')}_idx`
    const cols = columns.map((col) => `"${col}"`).join(', ')
    return `CREATE INDEX IF NOT EXISTS "${name}" ON "${config.table}"(${cols});`
  })
}

// Cursor + per-entity replica-shape versions live alongside the data so a
// wiped/reinstalled replica resets them together (no idb/sqlite desync).
const REPLICA_KV_DDL = `CREATE TABLE IF NOT EXISTS replica_kv (
  key TEXT PRIMARY KEY NOT NULL,
  value TEXT NOT NULL
);`

export function buildAllDDL(configs: readonly EntityReplicaConfig[]): string[] {
  const statements: string[] = []
  for (const config of configs) {
    statements.push(tableDDL(config))
    statements.push(...indexDDL(config))
  }
  statements.push(REPLICA_KV_DDL)
  statements.push(...USER_DATA_DDL)
  return statements
}
